// The date range every report and dashboard picks from: rolling windows ("Last
// 30 days"), calendar periods ("Month to date", "Last week") and any custom
// dates. Kept in the URL as ?range=<key> (a custom range adds &from&to) so a view
// can be shared and survives a reload. Days are the agency's calendar (US
// Eastern, like the server's utils/agencyTime); weeks start Monday, as there.
//
// No imports and only erasable types: Node loads this file directly in tests.

export type PresetKey = "today" | "7d" | "30d" | "90d" | "180d" | "365d" | "this_week" | "last_week" | "mtd" | "last_month";
export type DateRange = { key: PresetKey } | { key: "custom"; from: string; to: string };
/** Where a rolling window ends: yesterday on pages fed by the nightly sync, today on live ones. */
export type RollingEnd = "yesterday" | "today";

export const PRESET_LABEL: Record<PresetKey, string> = {
  today: "Today",
  "7d": "Last 7 days",
  "30d": "Last 30 days",
  "90d": "Last 90 days",
  "180d": "Last 180 days",
  "365d": "Last 365 days",
  this_week: "This week",
  last_week: "Last week",
  mtd: "Month to date",
  last_month: "Last month",
};

const ROLLING: Partial<Record<PresetKey, number>> = { "7d": 7, "30d": 30, "90d": 90, "180d": 180, "365d": 365 };
/** Days in a rolling preset, or null for a calendar one. */
export const rollingDays = (key: PresetKey | "custom") => (key === "custom" ? null : ROLLING[key] ?? null);

/** What the report pages offer. */
export const REPORT_PRESETS: PresetKey[] = ["7d", "30d", "90d", "this_week", "last_week", "mtd", "last_month"];

export const AGENCY_ZONE = "America/New_York";
/** The longest custom range: the Ads API takes up to 366 days. */
export const MAX_RANGE_DAYS = 366;

const YMD = /^\d{4}-\d{2}-\d{2}$/;
const DAY_MS = 86400000;
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export const isDay = (v: unknown): v is string =>
  typeof v === "string" && YMD.test(v) && new Date(`${v}T00:00:00Z`).toISOString().slice(0, 10) === v;

/** Today in `zone`, as YYYY-MM-DD. */
export function todayIn(zone = AGENCY_ZONE, now = new Date()): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: zone, year: "numeric", month: "2-digit", day: "2-digit" }).format(now);
}

// Calendar arithmetic on YYYY-MM-DD strings, in UTC so no local zone or DST change moves a day.
const parts = (ymd: string) => ymd.split("-").map(Number);
const utc = (y: number, m: number, d: number) => new Date(Date.UTC(y, m, d)).toISOString().slice(0, 10);
export const addDays = (ymd: string, n: number) => { const [y, m, d] = parts(ymd); return utc(y, m - 1, d + n); };
export const addMonths = (ymd: string, n: number) => { const [y, m, d] = parts(ymd); return utc(y, m - 1 + n, d); };
export const startOfMonth = (ymd: string) => { const [y, m] = parts(ymd); return utc(y, m - 1, 1); };
export const endOfMonth = (ymd: string) => { const [y, m] = parts(ymd); return utc(y, m, 0); };
/** 0 = Monday ... 6 = Sunday. */
export const weekdayMon = (ymd: string) => (new Date(`${ymd}T00:00:00Z`).getUTCDay() + 6) % 7;
export const startOfWeek = (ymd: string) => addDays(ymd, -weekdayMon(ymd));
/** Days from `from` to `to`, both counted. */
export const spanDays = (from: string, to: string) => Math.round((Date.parse(to) - Date.parse(from)) / DAY_MS) + 1;

/** The first and last day a range covers, both included. */
export function resolveRange(range: DateRange, today: string, rollingEnd: RollingEnd = "yesterday"): { from: string; to: string } {
  if (range.key === "custom") return { from: range.from, to: range.to };
  const days = ROLLING[range.key];
  if (days) {
    const to = rollingEnd === "today" ? today : addDays(today, -1);
    return { from: addDays(to, -(days - 1)), to };
  }
  switch (range.key) {
    case "today": return { from: today, to: today };
    case "this_week": return { from: startOfWeek(today), to: today };
    case "last_week": { const start = addDays(startOfWeek(today), -7); return { from: start, to: addDays(start, 6) }; }
    case "mtd": return { from: startOfMonth(today), to: today };
    case "last_month": { const start = addMonths(startOfMonth(today), -1); return { from: start, to: endOfMonth(start) }; }
  }
  return { from: today, to: today };
}

/** "Oct 3", or "Oct 3, 2025" outside `year`. */
export function shortDay(ymd: string, year?: string) {
  const [y, m, d] = parts(ymd);
  return `${MONTHS[m - 1]} ${d}${year && String(y) === year ? "" : `, ${y}`}`;
}

/** Both ends as "Sep 1", with years only when the range is not all this year. */
function ends(from: string, to: string, today: string): [string, string] {
  const year = today.slice(0, 4);
  const thisYear = from.slice(0, 4) === year && to.slice(0, 4) === year;
  return thisYear ? [shortDay(from, year), shortDay(to, year)] : [shortDay(from), shortDay(to)];
}

/** "Sep 1 to Sep 30" (the year shows only when it is not this year), "Oct 3" for one day. */
export function datesLabel(from: string, to: string, today: string) {
  const [a, b] = ends(from, to, today);
  return from === to ? a : `${a} to ${b}`;
}

/** The name on the picker: "Last 30 days", "Month to date", "Sep 1 to Sep 30". */
export function rangeLabel(range: DateRange, today: string) {
  return range.key === "custom" ? datesLabel(range.from, range.to, today) : PRESET_LABEL[range.key];
}

/** The range inside a sentence: "in the last 30 days", "this month so far", "between Sep 1 and Sep 30". */
export function rangePhrase(range: DateRange, today: string) {
  const days = rollingDays(range.key);
  if (days) return `in the last ${days} days`;
  switch (range.key) {
    case "today": return "today";
    case "this_week": return "this week so far";
    case "last_week": return "last week";
    case "mtd": return "this month so far";
    case "last_month": return "last month";
  }
  const { from, to } = resolveRange(range, today);
  const [a, b] = ends(from, to, today);
  return from === to ? `on ${a}` : `between ${a} and ${b}`;
}

/** Whether a custom range is one the APIs accept: real days, in order, not too long. */
export function customError(from: string, to: string, maxDays = MAX_RANGE_DAYS): string | null {
  if (!isDay(from) || !isDay(to)) return "Pick a start and an end date.";
  if (from > to) return "The start date is after the end date.";
  if (spanDays(from, to) > maxDays) return `Choose ${maxDays} days or fewer.`;
  return null;
}

/**
 * The range the URL asks for. ?range=<preset>, or ?range=custom&from&to. Older
 * links still work: ?days=30 is "Last 30 days" and ?from&to alone is custom.
 * Anything else falls back, never to an error.
 */
export function readDateRange(search: URLSearchParams, presets: readonly PresetKey[], fallback: PresetKey, maxDays = MAX_RANGE_DAYS): DateRange {
  const key = search.get("range");
  const from = search.get("from");
  const to = search.get("to");
  if ((key === "custom" || (!key && from && to)) && from && to && !customError(from, to, maxDays)) return { key: "custom", from, to };
  if (key && (presets as readonly string[]).includes(key)) return { key: key as PresetKey };
  const days = search.get("days");
  if (!key && days && (presets as readonly string[]).includes(`${days}d`)) return { key: `${days}d` as PresetKey };
  return { key: fallback };
}

/** The URL params for a range; `days` is dropped so an old link's value cannot linger. */
export function rangeParams(range: DateRange): Record<string, string | null> {
  return range.key === "custom"
    ? { range: "custom", from: range.from, to: range.to, days: null }
    : { range: range.key, from: null, to: null, days: null };
}

/** The same range as a query string, for a link to another report page. */
export function rangeSearch(range: DateRange) {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(rangeParams(range))) if (v !== null) q.set(k, v);
  return q.toString();
}

/**
 * The API's query for a range: a rolling window as ?days=N (the server ends it on
 * the newest synced day), anything else as the exact ?from&to days.
 */
export function rangeApiQuery(range: DateRange, today: string, rollingEnd: RollingEnd = "yesterday") {
  const days = rollingDays(range.key);
  if (days) return `days=${days}`;
  const { from, to } = resolveRange(range, today, rollingEnd);
  return `from=${from}&to=${to}`;
}

/** A report page's API query from its URL (Portfolio > Ads, a client's Ads report, Facebook). */
export const reportRangeQuery = (search: URLSearchParams, today = todayIn()) =>
  rangeApiQuery(readDateRange(search, REPORT_PRESETS, "30d"), today);

/** The weeks of `month` (any day in it) for a calendar, Monday first; null pads the edges. */
export function monthGrid(month: string): (string | null)[][] {
  const first = startOfMonth(month);
  const last = endOfMonth(month);
  const cells: (string | null)[] = Array(weekdayMon(first)).fill(null);
  for (let d = first; d <= last; d = addDays(d, 1)) cells.push(d);
  while (cells.length % 7) cells.push(null);
  const weeks: (string | null)[][] = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  return weeks;
}

/** "October 2026". */
export const monthTitle = (month: string) => {
  const [y, m] = parts(month);
  return `${["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"][m - 1]} ${y}`;
};
